import { intl } from '@beelday/common'
import { ConfiguredPresentButton } from 'presenter-mode/configured-present-button'
import { useMemo } from 'react'
import { useResumeTraining } from 'room/common/presentation/resume-training-menu-item'
import { useStageSurface } from 'room/common/stage-surface'
import ProfileAvatar from 'users/profile-avatar'
import { useProfileFor, useProfilesFor } from 'users/redux'
import breakPicture from './images/lobby-break.png'
import notStartedPicture from './images/lobby-not-started.png'
import { InPersonLobbyMoment } from './in-person-lobby-moment'
import { Message } from './lobby-view'
import { LobbyAction } from './presentation/lobby-action'
import { LobbyAttendance } from './presentation/lobby-attendance'
import { FactItem, LobbyFacts } from './presentation/lobby-facts'
import { LobbyHero } from './presentation/lobby-hero'
import { LobbySize } from './presentation/lobby-size'
import { LobbyStatus } from './presentation/lobby-status'
import { LobbySteps, StepItem } from './presentation/lobby-steps'
import { useLobbyScreen } from './use-lobby-view'

const PICTURES = {
	'not-started': notStartedPicture,
	break: breakPicture,
}

/** The faces beside the count; the rest is "+n". */
const FACES = 5

const text = ({ key, values }: Message): JSX.Element => (
	<intl.Translate {...values}>{key}</intl.Translate>
)

type Props = {
	/** The phone frame, or a laptop's room - and the projector, which is one. */
	layout: 'phone' | 'desktop'
	/** Read once by the screen: the room's menu and this card must agree. */
	lobby: InPersonLobbyMoment
}

/**
 * The in-person lobby's card for every audience: the trainer's checklist and
 * Start (or End break), the participants' and the projector's "we are
 * waiting". What it says is `lobbyView`; this reads the store, draws the
 * faces and hands the trainer's action the one request the menu's Start uses.
 */
export const ConfiguredLobbyHero = ({
	layout,
	lobby,
}: Props): JSX.Element | null => {
	const screen = useLobbyScreen(lobby)
	const surface = useStageSurface()
	const resume = useResumeTraining(lobby.moment)
	const translate = intl.useTranslate()
	const trainer = useProfileFor(screen?.trainerId)
	// Only the trainer counts the room, so only their screen needs the faces.
	const presentIds = screen?.role === 'trainer' ? screen.presentIds : undefined
	const facesOf = useMemo(() => presentIds?.slice(0, FACES) ?? [], [presentIds])
	const faces = useProfilesFor(facesOf)

	if (!screen) {
		return null
	}
	const { view } = screen
	const size: LobbySize =
		layout === 'phone' ? 'phone' : surface === 'projector' ? 'room' : 'desktop'

	const attendance = (message: Message): JSX.Element => (
		<LobbyAttendance
			faces={faces.map((face, index) => (
				<ProfileAvatar key={face?.id ?? index} profile={face} />
			))}
			more={Math.max(0, screen.presentIds.length - FACES)}
		>
			{text(message)}
		</LobbyAttendance>
	)

	const facts: FactItem[] = view.facts.map(fact => ({
		id: fact.id,
		text: text(fact.message),
		leading:
			fact.id === 'led-by' ? <ProfileAvatar profile={trainer} /> : undefined,
	}))

	const steps: StepItem[] = view.steps.map(step => ({
		id: step.id,
		title: text(step.title),
		description: text(step.description),
		done: step.done,
		extra:
			step.id === 'present' ? (
				<ConfiguredPresentButton />
			) : step.attendance ? (
				attendance(step.attendance)
			) : undefined,
	}))

	return (
		<LobbyHero
			size={size}
			illustration={PICTURES[view.illustration]}
			eyebrow={text(view.eyebrow)}
			heading={text(view.heading)}
			subject={view.meta.subject}
			hours={view.meta.hours}
			facts={<LobbyFacts size={size} facts={facts} />}
		>
			{steps.length > 0 ? (
				<LobbySteps
					size={size}
					label={translate('in_person.lobby.steps.label')}
					doneLabel={<intl.Translate>in_person.lobby.step.done</intl.Translate>}
					steps={steps}
				/>
			) : null}
			{view.attendance ? attendance(view.attendance) : null}
			{view.status ? (
				<LobbyStatus size={size}>{text(view.status)}</LobbyStatus>
			) : null}
			{view.action ? (
				<LobbyAction
					onClick={resume}
					testId={`in_person.lobby.${view.action.id}`}
				>
					{text(view.action.label)}
				</LobbyAction>
			) : null}
		</LobbyHero>
	)
}
